import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Edit,
  Trash2,
  Package,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Globe,
  FileText,
} from 'lucide-react';

/**
 * Returns a coloured pill badge for a shipping type.
 * Kept here so the card is self-contained.
 */
const getTypeBadge = (type, t) => {
  const types = {
    standard:     { color: '#3b82f6', label: t('admin.shipping.typeStandard') },
    express:      { color: '#f59e0b', label: t('admin.shipping.typeExpress') },
    overnight:    { color: '#ef4444', label: t('admin.shipping.typeOvernight') },
    local_pickup: { color: '#10b981', label: t('admin.shipping.typeLocalPickup') },
  };
  const typeInfo = types[type] || types.standard;
  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        background: `${typeInfo.color}20`,
        color: typeInfo.color,
      }}
    >
      {typeInfo.label}
    </span>
  );
};

/**
 * ShippingMethodCard — displays one shipping method row/card.
 *
 * Props:
 *   method           — shipping method object
 *   onEdit           — (method) => void
 *   onDelete         — (methodId) => void
 *   onToggle         — (methodId) => void
 *   onGenerateLabel  — (method) => void
 */
const ShippingMethodCard = ({ method, onEdit, onDelete, onToggle, onGenerateLabel }) => {
  const { t } = useTranslation();

  return (
    <div className="quote-card">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="quote-header">
        <div>
          <h3 style={{ marginBottom: '0.25rem' }}>{method.name}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <p className="quote-customer" style={{ margin: 0 }}>{method.carrier}</p>
            {method.carrierCode && (
              <span style={{
                fontSize: '11px',
                padding: '2px 6px',
                background: '#1e293b',
                borderRadius: '4px',
                color: '#94a3b8',
                fontWeight: '600',
              }}>
                {method.carrierCode}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {getTypeBadge(method.type, t)}
          {method.isActive
            ? <CheckCircle size={20} color="#10b981" />
            : <XCircle size={20} color="#ef4444" />
          }
        </div>
      </div>

      {/* ── Detail pills ───────────────────────────────────── */}
      <div className="quote-details">
        <div className="quote-detail-item">
          <Package size={16} />
          <span>{method.minWeight || 0}kg - {method.maxWeight}kg</span>
        </div>
        <div className="quote-detail-item">
          <MapPin size={16} />
          <span>{method.zones.length} {t('admin.shipping.card.zones')}</span>
        </div>
        {method.handlingTime > 0 && (
          <div className="quote-detail-item">
            <Clock size={16} />
            <span>{method.handlingTime}d {t('admin.shipping.card.handling')}</span>
          </div>
        )}
        {method.trackingAvailable && (
          <div className="quote-detail-item" style={{ color: '#38bdf8' }}>
            <Globe size={16} />
            <span>{t('admin.shipping.card.tracking')}</span>
          </div>
        )}
        {method.insuranceIncluded && (
          <div className="quote-detail-item" style={{ color: '#22c55e' }}>
            <CheckCircle size={16} />
            <span>{t('admin.shipping.card.insurance')}</span>
          </div>
        )}
        {method.deliveryGuarantee && (
          <div className="quote-detail-item" style={{ color: '#f59e0b' }}>
            <CheckCircle size={16} />
            <span>{t('admin.shipping.card.guaranteed')}</span>
          </div>
        )}
      </div>

      {/* ── Tracking & carrier info block ──────────────────── */}
      {(method.trackingUrlTemplate || method.carrierWebsite || method.carrierPhone) && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.75rem',
          background: '#1e293b',
          borderRadius: '6px',
          fontSize: '13px',
        }}>
          {method.trackingUrlTemplate && (
            <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Globe size={14} color="#38bdf8" />
              <span style={{ color: '#94a3b8' }}>{t('admin.shipping.card.trackingUrl')}:</span>
              <code style={{
                fontSize: '11px',
                background: '#0f172a',
                padding: '2px 6px',
                borderRadius: '3px',
                color: '#38bdf8',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {method.trackingUrlTemplate}
              </code>
            </div>
          )}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', color: '#94a3b8' }}>
            {method.carrierWebsite && (
              <a
                href={method.carrierWebsite}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#38bdf8', textDecoration: 'none', fontSize: '12px' }}
              >
                🌐 Website
              </a>
            )}
            {method.carrierPhone && (
              <span style={{ fontSize: '12px' }}>📞 {method.carrierPhone}</span>
            )}
            {method.carrierEmail && (
              <span style={{ fontSize: '12px' }}>✉️ {method.carrierEmail}</span>
            )}
          </div>
        </div>
      )}

      {/* ── Additional feature badges ───────────────────────── */}
      {(method.saturdayDelivery || method.sundayDelivery || method.customsClearance) && (
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {method.saturdayDelivery && (
            <span style={{
              fontSize: '11px', padding: '3px 8px',
              background: '#166534', color: '#86efac',
              borderRadius: '4px', fontWeight: '600',
            }}>SAT ✓</span>
          )}
          {method.sundayDelivery && (
            <span style={{
              fontSize: '11px', padding: '3px 8px',
              background: '#166534', color: '#86efac',
              borderRadius: '4px', fontWeight: '600',
            }}>SUN ✓</span>
          )}
          {method.customsClearance && (
            <span style={{
              fontSize: '11px', padding: '3px 8px',
              background: '#1e40af', color: '#93c5fd',
              borderRadius: '4px', fontWeight: '600',
            }}>CUSTOMS ✓</span>
          )}
        </div>
      )}

      {/* ── Description ────────────────────────────────────── */}
      {method.description && (
        <p style={{ fontSize: '13px', color: '#a1a1aa', marginTop: '0.75rem', lineHeight: '1.5' }}>
          {method.description}
        </p>
      )}

      {/* ── Action buttons ─────────────────────────────────── */}
      <div className="quote-actions">
        <button
          onClick={() => onGenerateLabel(method)}
          className="action-btn"
          title={t('admin.shipping.actions.generateLabel')}
          aria-label="Genera Etichetta"
          style={{ color: '#3b82f6' }}
        >
          <FileText size={16} />
        </button>
        <button
          onClick={() => onToggle(method._id)}
          className="action-btn"
          title={t('admin.shipping.actions.toggleActive')}
          aria-label={method.isActive ? t('admin.automation.disable') : t('admin.automation.enable')}
        >
          {method.isActive ? <XCircle size={16} /> : <CheckCircle size={16} />}
        </button>
        <button
          onClick={() => onEdit(method)}
          className="action-btn"
          title={t('admin.common.edit')}
          aria-label={t('admin.common.edit')}
        >
          <Edit size={16} />
        </button>
        <button
          onClick={() => onDelete(method._id)}
          className="action-btn"
          title={t('admin.common.delete')}
          aria-label={t('admin.common.delete')}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default ShippingMethodCard;
